<template>
  <div class="tenantDetail">
    <div class="content-container">
      <div class="titieMian">
        <div class="title-container">
          承租方详情
        </div>
      </div>

      <div class="table-container detail-body" v-loading="loading">
        <!-- 承租方信息 -->
        <div class="detail-section">
          <div class="detail-section-title">承租方信息</div>
          <div class="detail-grid3">
            <div class="detail-row">
              <span class="detail-label">承租方名称</span>
              <span class="detail-value">{{ detail.tenantName || "-" }}</span>
            </div>
            <div class="detail-row">
              <span class="detail-label">类型</span>
              <span class="detail-value">{{
                enumLabel("tenantType", detail.tenantType)
              }}</span>
            </div>
            <div class="detail-row">
              <span class="detail-label">登录帐号</span>
              <span class="detail-value">{{ detail.loginAccount || "-" }}</span>
            </div>
            <div class="detail-row">
              <span class="detail-label">状态</span>
              <span class="detail-value">
                <LeaseStatusTag kind="tenant" :value="detail.status" />
              </span>
            </div>
            <div class="detail-row">
              <span class="detail-label">审核状态</span>
              <span class="detail-value">
                <LeaseStatusTag kind="audit" :value="detail.auditStatus" />
              </span>
            </div>
            <div
              v-if="detail.auditRemark"
              class="detail-row detail-row-full"
            >
              <span class="detail-label">审核驳回原因</span>
              <span class="detail-value detail-value-danger">{{
                detail.auditRemark
              }}</span>
            </div>
          </div>
        </div>

        <!-- 企业/个体信息（非个人） -->
        <div v-if="isOrgTenant" class="detail-section">
          <div class="detail-section-title">企业/个体信息</div>
          <div class="detail-grid3">
            <div class="detail-row">
              <span class="detail-label">企业简称</span>
              <span class="detail-value">{{ detail.shortName || "-" }}</span>
            </div>
            <div class="detail-row">
              <span class="detail-label">统一社会信用代码</span>
              <span class="detail-value">{{ detail.creditCode || "-" }}</span>
            </div>
            <div class="detail-row">
              <span class="detail-label">开户银行</span>
              <span class="detail-value">{{ detail.bankName || "-" }}</span>
            </div>
            <div class="detail-row">
              <span class="detail-label">银行账号</span>
              <span class="detail-value">{{ detail.bankAccount || "-" }}</span>
            </div>
          </div>
        </div>

        <!-- 联系人信息 -->
        <div class="detail-section">
          <div class="detail-section-title">联系人信息</div>
          <div class="detail-grid3">
            <template v-if="isOrgTenant">
              <div class="detail-row">
                <span class="detail-label">法人/负责人姓名</span>
                <span class="detail-value">{{ detail.legalPerson || "-" }}</span>
              </div>
              <div class="detail-row">
                <span class="detail-label">法人/负责人电话</span>
                <span class="detail-value">{{ detail.legalPhone || "-" }}</span>
              </div>
            </template>
            <div class="detail-row">
              <span class="detail-label">联系人姓名</span>
              <span class="detail-value">{{ detail.contactName || "-" }}</span>
            </div>
            <div class="detail-row">
              <span class="detail-label">联系人电话</span>
              <span class="detail-value">{{ detail.contactPhone || "-" }}</span>
            </div>
            <div class="detail-row">
              <span class="detail-label">联系人邮箱</span>
              <span class="detail-value">{{ detail.contactEmail || "-" }}</span>
            </div>
            <div class="detail-row">
              <span class="detail-label">联系人身份证</span>
              <span class="detail-value">{{ detail.contactIdCard || "-" }}</span>
            </div>
            <div class="detail-row detail-row-full">
              <span class="detail-label">居住地址</span>
              <span class="detail-value">{{ detail.address || "-" }}</span>
            </div>
          </div>
        </div>

        <!-- 附件（对齐源系统 systemFileList） -->
        <div v-if="attachmentList.length" class="detail-section">
          <div class="detail-section-title">附件</div>
          <div
            v-for="(file, idx) in attachmentList"
            :key="idx"
            class="detail-file-row"
          >
            <a
              class="detail-file-link"
              :href="file.url"
              target="_blank"
              rel="noopener noreferrer"
              >{{ file.fileName }}</a
            >
          </div>
        </div>

        <!-- 合同历史 -->
        <div class="detail-section">
          <div class="detail-section-title">合同历史</div>
          <el-table
            v-loading="contractLoading"
            :data="contractList"
            border
            size="small"
            style="width:100%"
            :header-cell-style="{ background: '#f5f7fa', color: '#606266' }"
          >
            <i slot="empty" style="font-style:normal">暂无合同历史</i>
            <el-table-column
              type="index"
              label="序号"
              width="60"
              align="center"
            />
            <el-table-column
              prop="contractCode"
              label="合同编号"
              min-width="160"
              show-overflow-tooltip
            />
            <el-table-column
              prop="contractName"
              label="合同名称"
              min-width="140"
              show-overflow-tooltip
            />
            <el-table-column label="合同状态" width="100" align="center">
              <template slot-scope="scope">
                <LeaseStatusTag kind="contract" :value="scope.row.contractStatus" />
              </template>
            </el-table-column>
            <el-table-column
              prop="totalRent"
              label="鱼塘租金(元)"
              width="120"
              align="center"
            />
            <el-table-column label="合同时间" min-width="200" align="center">
              <template slot-scope="scope">
                {{ formatDate(scope.row.contractStart) }} 至
                {{ formatDate(scope.row.contractEnd) }}
              </template>
            </el-table-column>
            <el-table-column
              prop="address"
              label="鱼塘位置"
              min-width="160"
              show-overflow-tooltip
            >
              <template slot-scope="scope">{{
                scope.row.address ||
                  scope.row.propertyAddress ||
                  "-"
              }}</template>
            </el-table-column>
            <el-table-column label="缴费情况" width="100" align="center">
              <template slot-scope="scope">
                <LeaseStatusTag kind="payment" :value="scope.row.paymentStatus" />
              </template>
            </el-table-column>
            <el-table-column label="已缴次数" width="90" align="center">
              <template slot-scope="scope">{{
                scope.row.payCount != null
                  ? scope.row.payCount
                  : scope.row.paymentPeriod != null
                    ? scope.row.paymentPeriod
                    : "-"
              }}</template>
            </el-table-column>
          </el-table>
        </div>

        <!-- 审核流程记录（时间轴，对齐源系统） -->
        <div class="detail-section">
          <div class="detail-section-title">审核流程记录</div>
          <div v-loading="auditLogLoading">
            <div v-if="auditLogs.length" class="tl">
              <div
                v-for="(log, idx) in auditLogs"
                :key="log.id || idx"
                class="tl-node"
              >
                <div class="tl-dot" :class="getLogDotClass(log)"></div>
                <div class="tl-content">
                  <div class="tl-title">{{ getLogTitle(log) }}</div>
                  <div class="tl-info">
                    <span class="tl-info-item"
                      >操作人：{{ log.operatorName || "-" }}</span
                    >
                    <span class="tl-info-item"
                      >操作方：{{ log.enterpriseName || "-" }}</span
                    >
                    <span class="tl-info-item"
                      >时间：{{ formatTime(log.actionTime) || "-" }}</span
                    >
                  </div>
                  <div
                    v-if="log.auditRemark && isRejectAction(log)"
                    class="tl-result tl-result--reject"
                  >
                    驳回原因：{{ log.auditRemark }}
                  </div>
                </div>
                <div
                  v-if="idx < auditLogs.length - 1"
                  class="tl-line"
                  :class="getLogLineClass(log)"
                ></div>
              </div>
            </div>
            <div v-else-if="!auditLogLoading" class="empty-tip">
              暂无审核记录
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import {
  getTenant,
  listAuditLog,
  pageContract,
  leaseRequestError,
  resolveLeaseResourceUrl
} from "@/service/fishPondLease.js";
import {
  TenantType,
  AuditBusType,
  AuditActionType,
  enumLabel
} from "@/service/fishPondLeaseEnums.js";
import LeaseStatusTag from "@/components/fishPondLease/common/LeaseStatusTag.vue";

export default {
  name: "TenantDetail",
  components: {
    LeaseStatusTag: LeaseStatusTag
  },
  data: function() {
    return {
      loading: false,
      contractLoading: false,
      auditLogLoading: false,
      detail: {},
      auditLogs: [],
      contractList: []
    };
  },
  computed: {
    tenantId: function() {
      return (
        (this.$route.query && this.$route.query.id) ||
        (this.$route.params && this.$route.params.id) ||
        ""
      );
    },
    isOrgTenant: function() {
      var t = Number(this.detail.tenantType);
      return t === TenantType.Enterprise || t === TenantType.SelfEmployed;
    },
    attachmentList: function() {
      var raw = this.detail.attachmentUrl || "";
      if (!raw) return [];
      return String(raw)
        .split(",")
        .map(function(s) {
          return s.trim();
        })
        .filter(Boolean)
        .map(function(item, index) {
          return {
            fileName: item.split("/").pop() || "附件" + (index + 1),
            url: resolveLeaseResourceUrl(item)
          };
        });
    }
  },
  mounted: function() {
    this.loadDetail();
  },
  methods: {
    enumLabel: enumLabel,
    formatTime: function(val) {
      if (!val) return "";
      return String(val)
        .replace("T", " ")
        .substring(0, 19);
    },
    formatDate: function(val) {
      if (!val) return "-";
      return String(val)
        .replace("T", " ")
        .substring(0, 10);
    },
    isRejectAction: function(log) {
      var t = log.actionType;
      return (
        Number(t) === AuditActionType.Reject ||
        t === "REJECT" ||
        t === "Reject"
      );
    },
    getLogTitle: function(log) {
      var t = log.actionType;
      if (Number(t) === AuditActionType.Submit || t === "SUBMIT") {
        return "提交审核申请";
      }
      if (Number(t) === AuditActionType.Approve || t === "APPROVE") {
        return "审核通过";
      }
      if (Number(t) === AuditActionType.Reject || t === "REJECT") {
        return "审核驳回";
      }
      return log.actionTypeCn || "-";
    },
    getLogDotClass: function(log) {
      if (this.isRejectAction(log)) return "tl-dot--reject";
      var t = log.actionType;
      if (
        Number(t) === AuditActionType.Submit ||
        Number(t) === AuditActionType.Approve ||
        t === "SUBMIT" ||
        t === "APPROVE"
      ) {
        return "tl-dot--done";
      }
      return "tl-dot--pending";
    },
    getLogLineClass: function(log) {
      if (this.isRejectAction(log)) return "tl-line--reject";
      return "tl-line--done";
    },
    goBack: function() {
      this.$router.back();
    },
    loadDetail: function() {
      var self = this;
      var id = this.tenantId;
      if (!id) {
        this.$message.warning("缺少承租方Id");
        return;
      }
      this.loading = true;
      getTenant(id)
        .then(function(res) {
          self.detail = (res && res.data) || {};
          self.loadContractList();
          self.loadAuditLog();
        })
        .catch(function(e) {
          self.$message.error(leaseRequestError(e));
        })
        .then(function() {
          self.loading = false;
        });
    },
    loadContractList: function() {
      var self = this;
      var id = this.tenantId;
      if (!id) return;
      this.contractLoading = true;
      pageContract({
        tenantId: id,
        pageIndex: 1,
        pageSize: 100
      })
        .then(function(res) {
          var data = (res && res.data) || {};
          self.contractList = data.list || [];
        })
        .catch(function(e) {
          self.$message.error(leaseRequestError(e));
        })
        .then(function() {
          self.contractLoading = false;
        });
    },
    loadAuditLog: function() {
      var self = this;
      var id = this.tenantId;
      if (!id) return;
      this.auditLogLoading = true;
      listAuditLog({ busType: AuditBusType.Tenant, busId: id })
        .then(function(res) {
          var data = (res && res.data) || [];
          self.auditLogs = Array.isArray(data) ? data : [];
        })
        .catch(function(e) {
          self.$message.error(leaseRequestError(e));
        })
        .then(function() {
          self.auditLogLoading = false;
        });
    }
  }
};
</script>

<style scoped>
.detail-body {
  padding: 16px 20px 24px;
  background: #fff;
}

.detail-section {
  margin-bottom: 20px;
  padding-bottom: 8px;
  border-bottom: 1px solid #ebeef5;
}

.detail-section:last-child {
  border-bottom: none;
  margin-bottom: 0;
}

.detail-section-title {
  font-size: 15px;
  font-weight: 600;
  color: #303133;
  margin-bottom: 12px;
  line-height: 22px;
}

.detail-grid3 {
  display: flex;
  flex-wrap: wrap;
}

.detail-row {
  width: 33.33%;
  box-sizing: border-box;
  display: flex;
  align-items: flex-start;
  padding: 8px 12px 8px 0;
  min-height: 32px;
}

.detail-row-full {
  width: 100%;
}

.detail-label {
  flex: 0 0 120px;
  color: #909399;
  font-size: 13px;
  line-height: 20px;
}

.detail-value {
  flex: 1;
  color: #303133;
  font-size: 13px;
  line-height: 20px;
  word-break: break-all;
}

.detail-value-danger {
  color: #f56c6c;
}

.detail-file-row {
  padding: 4px 0 8px;
}

.detail-file-link {
  color: #409eff;
  font-size: 13px;
  text-decoration: none;
  word-break: break-all;
}

.detail-file-link:hover {
  text-decoration: underline;
}

.empty-tip {
  font-size: 14px;
  color: #909399;
  text-align: center;
  padding: 32px 0;
}

.tl {
  max-width: 720px;
}

.tl-node {
  display: flex;
  position: relative;
}

.tl-dot {
  width: 22px;
  height: 22px;
  border-radius: 50%;
  flex-shrink: 0;
  margin-right: 12px;
  display: flex;
  align-items: center;
  justify-content: center;
  position: relative;
  z-index: 1;
  color: #fff;
  font-size: 12px;
  line-height: 1;
}

.tl-dot--done {
  background: #67c23a;
}

.tl-dot--done::before {
  content: "\2713";
}

.tl-dot--pending {
  background: #909399;
}

.tl-dot--reject {
  background: #f56c6c;
  font-size: 14px;
  font-weight: bold;
}

.tl-dot--reject::before {
  content: "\00d7";
}

.tl-line {
  position: absolute;
  left: 10px;
  top: 22px;
  width: 2px;
  height: 100%;
  background: #e4e7ed;
}

.tl-line--done {
  background: #67c23a;
}

.tl-line--reject {
  background: #f56c6c;
}

.tl-content {
  flex: 1;
  min-width: 0;
  padding-bottom: 20px;
}

.tl-title {
  font-size: 14px;
  font-weight: 600;
  color: #303133;
  margin-bottom: 8px;
  line-height: 22px;
}

.tl-info {
  display: flex;
  gap: 24px;
  flex-wrap: wrap;
}

.tl-info-item {
  font-size: 13px;
  color: #606266;
}

.tl-result {
  margin-top: 10px;
  font-size: 13px;
  font-weight: 500;
  color: #606266;
}

.tl-result--reject {
  color: #f56c6c;
  background: #fef0f0;
  border: 1px solid #fde2e2;
  border-left: 3px solid #f56c6c;
  border-radius: 4px;
  padding: 10px 14px;
  line-height: 1.5;
}

@media screen and (max-width: 1100px) {
  .detail-row {
    width: 50%;
  }
}

@media screen and (max-width: 768px) {
  .detail-row {
    width: 100%;
  }
}
</style>
